import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { 
    addDoc, collection, Timestamp, query, getDocs, updateDoc, 
    deleteDoc, doc, orderBy, where,
} from "firebase/firestore";

import { 
    Container, TextField, Button, Typography, Box, Paper, Alert, 
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Divider, CircularProgress, Grid, Chip, IconButton, Stack, Snackbar
} from "@mui/material";
import { styled } from "@mui/system";

// Icone
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CampaignIcon from '@mui/icons-material/Campaign';
import SendIcon from '@mui/icons-material/Send';
import DateRangeIcon from '@mui/icons-material/DateRange';
import HistoryIcon from '@mui/icons-material/History';

// --- STYLED COMPONENTS ---

const MainContainer = styled(Container)({
    paddingTop: "40px",
    paddingBottom: "40px",
    fontFamily: "'Inter', sans-serif",
});

const SectionTitle = styled(Typography)({
    fontWeight: 800,
    fontSize: "1.5rem",
    marginBottom: "20px",
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
});

const ModernCard = styled(Paper)({
    background: "#FFFFFF",
    borderRadius: "24px",
    boxShadow: "0px 10px 40px rgba(0,0,0,0.04)",
    padding: "32px",
    height: '100%'
});

// Card specifica per la "Zona Pericolo"
const DangerCard = styled(Paper)({
    background: "#FFF5F5", // Sfondo rossastro
    border: "2px solid #ffcccc",
    borderRadius: "24px",
    padding: "32px",
    marginBottom: "40px"
});

// Card per singolo annuncio in lista
const AnnouncementItem = styled(Paper)({
    padding: "20px",
    borderRadius: "16px",
    border: "1px solid #f1f2f6",
    marginBottom: "16px",
    transition: "all 0.2s ease",
    "&:hover": {
        borderColor: "#a32b2b",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
    }
});

// Stile Input Arrotondato
const inputStyle = {
    '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' }
};

export default function ClosurePage() {
    
    // UTILS
    const getTodayDate = () => new Date().toISOString().split("T")[0];
    
    const formatTimestampToDate = (timestamp) => {
        if (!timestamp || !timestamp.toDate) return 'N/D';
        return timestamp.toDate().toLocaleDateString('it-IT');
    };

    // STATES
    const [cancellationStartDate, setCancellationStartDate] = useState("");
    const [cancellationEndDate, setCancellationEndDate] = useState("");
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [expirationDateStr, setExpirationDateStr] = useState("");
    const [isSending, setIsSending] = useState(false);
    
    const [announcements, setAnnouncements] = useState([]);
    const [loadingAnnouncements, setLoadingAnnouncements] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    
    const [feedback, setFeedback] = useState({ open: false, msg: "", type: "success" });

    // FETCH
    const fetchAnnouncements = async () => {
        setLoadingAnnouncements(true);
        try {
            const q = query(collection(db, "announcements"), orderBy("createdAt", "desc"));
            const snapshot = await getDocs(q);
            setAnnouncements(snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                expirationDateStr: doc.data().expirationDate ? doc.data().expirationDate.toDate().toISOString().split('T')[0] : '',
            })));
        } catch (err) { console.error(err); } 
        finally { setLoadingAnnouncements(false); }
    };

    useEffect(() => { fetchAnnouncements(); }, []);

    // HANDLERS ANNOUNCEMENTS
    const handleSendAnnouncement = async (e) => {
        e.preventDefault();
        if (!title || !message) return setFeedback({ open: true, msg: "Titolo e messaggio obbligatori", type: "error" });

        setIsSending(true);
        try {
            let expirationTimestamp = null;
            if (expirationDateStr) {
                const date = new Date(expirationDateStr);
                date.setHours(23, 59, 59, 999);
                expirationTimestamp = Timestamp.fromDate(date);
            }
            
            await addDoc(collection(db, "announcements"), {
                title, message, 
                expirationDate: expirationTimestamp, 
                createdAt: Timestamp.fromDate(new Date())
            });
            
            setFeedback({ open: true, msg: "Annuncio pubblicato!", type: "success" });
            setTitle(""); setMessage(""); setExpirationDateStr("");
            fetchAnnouncements();
        } catch (err) { setFeedback({ open: true, msg: "Errore pubblicazione", type: "error" }); } 
        finally { setIsSending(false); }
    };

    const handleDeleteAnnouncement = async () => {
        setDeleteConfirmationOpen(false);
        if (!currentAnnouncement) return;
        setIsSending(true);
        try {
            await deleteDoc(doc(db, "announcements", currentAnnouncement.id));
            setFeedback({ open: true, msg: "Annuncio eliminato", type: "success" });
            fetchAnnouncements();
        } catch (err) { setFeedback({ open: true, msg: "Errore eliminazione", type: "error" }); }
        finally { setIsSending(false); setCurrentAnnouncement(null); }
    };

    const handleSaveEdit = async () => {
        if (!currentAnnouncement.title || !currentAnnouncement.message) return alert("Compila i campi");
        setIsSending(true);
        try {
            let expirationTimestamp = null;
            if (currentAnnouncement.expirationDateStr) {
                const date = new Date(currentAnnouncement.expirationDateStr);
                date.setHours(23, 59, 59, 999);
                expirationTimestamp = Timestamp.fromDate(date);
            }
            await updateDoc(doc(db, "announcements", currentAnnouncement.id), {
                title: currentAnnouncement.title,
                message: currentAnnouncement.message,
                expirationDate: expirationTimestamp,
            });
            fetchAnnouncements();
            setIsEditing(false);
            setFeedback({ open: true, msg: "Modifica salvata", type: "success" });
        } catch (err) { setFeedback({ open: true, msg: "Errore modifica", type: "error" }); }
        finally { setIsSending(false); }
    };

    // HANDLER CANCELLATION
    const handleMassCancellation = async () => {
        setIsConfirmationOpen(false);
        if (!cancellationStartDate || !cancellationEndDate) return setFeedback({ open: true, msg: "Seleziona le date", type: "warning" });
        
        setIsCancelling(true);
        try {
            const q = query(
                collection(db, "userBookings"),
                where("dataPrenotazione", ">=", cancellationStartDate),
                where("dataPrenotazione", "<=", cancellationEndDate),
                where("status", "!=", "CANCELED_ADMIN"),
                where("status", "!=", "CANCELED_USER")
            );
            const snapshot = await getDocs(q);
            
            if (snapshot.empty) {
                setIsCancelling(false);
                return setFeedback({ open: true, msg: "Nessuna prenotazione trovata nel periodo.", type: "info" });
            }

            const promises = snapshot.docs.map(d => updateDoc(doc(db, "userBookings", d.id), {
                status: "CANCELED_ADMIN",
                cancellationReason: `Chiusura straordinaria ${cancellationStartDate} - ${cancellationEndDate}`
            }));
            
            await Promise.all(promises);
            setFeedback({ open: true, msg: `${promises.length} prenotazioni annullate con successo.`, type: "success" });
        } catch (err) { setFeedback({ open: true, msg: "Errore critico annullamento", type: "error" }); }
        finally { setIsCancelling(false); }
    };

    return (
        <MainContainer maxWidth="xl">
            <Box sx={{ mb: 5 }}>
                <Typography variant="h4" fontWeight="800" sx={{ color: "#2d3436" }}>Centro di Controllo</Typography>
                <Typography variant="body1" color="text.secondary">Gestisci emergenze e comunicazioni.</Typography>
            </Box>

            {/* --- SEZIONE 1: DANGER ZONE --- */}
            <DangerCard elevation={0}>
                <SectionTitle sx={{ color: "#c0392b" }}>
                    <WarningAmberIcon fontSize="large" /> 
                    Chiusura Straordinaria
                </SectionTitle>
                <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
                    Utilizza questo strumento per annullare massivamente le prenotazioni in caso di chiusura imprevista o ferie.
                    <br/>
                    <Box component="span" sx={{ fontSize: '0.9rem', color: '#e74c3c' }}>
                        * Le prenotazioni verranno segnate come "CANCELLATE DALL'ADMIN".
                    </Box>
                </Typography>

                <Grid container spacing={3} alignItems="flex-end">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth label="Dal giorno" type="date"
                            value={cancellationStartDate} onChange={e => setCancellationStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }} inputProps={{ min: getTodayDate() }}
                            sx={inputStyle}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth label="Al giorno" type="date"
                            value={cancellationEndDate} onChange={e => setCancellationEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }} inputProps={{ min: cancellationStartDate || getTodayDate() }}
                            sx={inputStyle}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Button 
                            fullWidth variant="contained" color="error" size="large"
                            onClick={() => setIsConfirmationOpen(true)}
                            disabled={isCancelling || !cancellationStartDate || !cancellationEndDate}
                            startIcon={isCancelling ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                            sx={{ borderRadius: '12px', height: '56px', fontWeight: 'bold' }}
                        >
                            {isCancelling ? 'Elaborazione...' : 'ANNULLA PRENOTAZIONI'}
                        </Button>
                    </Grid>
                </Grid>
            </DangerCard>

            {/* --- SEZIONE 2: COMUNICAZIONI --- */}
            <Grid container spacing={4}>
                
                {/* SINISTRA: NUOVO ANNUNCIO */}
                <Grid item xs={12} lg={5}>
                    <ModernCard>
                        <SectionTitle><CampaignIcon color="primary" /> Nuovo Avviso</SectionTitle>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Invia una notifica in bacheca a tutti gli utenti.
                        </Typography>
                        
                        <form onSubmit={handleSendAnnouncement}>
                            <Stack spacing={3}>
                                <TextField
                                    label="Titolo (es. Chiusura per feste)" required fullWidth
                                    value={title} onChange={e => setTitle(e.target.value)}
                                    sx={inputStyle}
                                />
                                <TextField
                                    label="Messaggio" required fullWidth multiline rows={4}
                                    value={message} onChange={e => setMessage(e.target.value)}
                                    sx={inputStyle}
                                />
                                <TextField
                                    label="Scadenza Avviso (Opzionale)" type="date" fullWidth
                                    value={expirationDateStr} onChange={e => setExpirationDateStr(e.target.value)}
                                    InputLabelProps={{ shrink: true }} inputProps={{ min: getTodayDate() }}
                                    sx={inputStyle}
                                />
                                <Button 
                                    type="submit" variant="contained" size="large"
                                    disabled={isSending} startIcon={<SendIcon />}
                                    sx={{ 
                                        borderRadius: '12px', py: 1.5,
                                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                        boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                                    }}
                                >
                                    PUBBLICA AVVISO
                                </Button>
                            </Stack>
                        </form>
                    </ModernCard>
                </Grid>

                {/* DESTRA: STORICO ANNUNCI */}
                <Grid item xs={12} lg={7}>
                    <ModernCard sx={{ bgcolor: '#F8F9FA' }}> {/* Sfondo leggermente diverso per l'area lista */}
                        <SectionTitle><HistoryIcon color="primary" /> Storico Avvisi</SectionTitle>
                        
                        {loadingAnnouncements ? (
                            <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>
                        ) : announcements.length === 0 ? (
                            <Alert severity="info" sx={{ borderRadius: '12px' }}>Non ci sono avvisi attivi.</Alert>
                        ) : (
                            <Box sx={{ maxHeight: '600px', overflowY: 'auto', pr: 1 }}>
                                {announcements.map((ann) => {
                                    const isExpired = ann.expirationDate && ann.expirationDate.toDate() < new Date();
                                    return (
                                        <AnnouncementItem key={ann.id} elevation={0}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Typography variant="h6" fontWeight="bold" color={isExpired ? 'text.secondary' : 'text.primary'}>
                                                    {ann.title}
                                                </Typography>
                                                <Box>
                                                    <IconButton size="small" onClick={() => { setCurrentAnnouncement(ann); setIsEditing(true); }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => { setCurrentAnnouncement(ann); setDeleteConfirmationOpen(true); }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                            
                                            <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-line', color: '#555' }}>
                                                {ann.message}
                                            </Typography>
                                            
                                            <Divider sx={{ mb: 2 }} />
                                            
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <Chip 
                                                    label={isExpired ? "Scaduto" : "Attivo"} 
                                                    color={isExpired ? "default" : "success"} 
                                                    size="small" variant="outlined"
                                                />
                                                {ann.expirationDate && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <DateRangeIcon fontSize="inherit" />
                                                        Scade il: {formatTimestampToDate(ann.expirationDate)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </AnnouncementItem>
                                    );
                                })}
                            </Box>
                        )}
                    </ModernCard>
                </Grid>
            </Grid>

            {/* --- DIALOGS --- */}
            {/* DELETE CONFIRM */}
            <Dialog open={deleteConfirmationOpen} onClose={() => setDeleteConfirmationOpen(false)}>
                <DialogTitle>Eliminare avviso?</DialogTitle>
                <DialogContent><DialogContentText>L'avviso "{currentAnnouncement?.title}" verrà rimosso permanentemente.</DialogContentText></DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmationOpen(false)}>Annulla</Button>
                    <Button onClick={handleDeleteAnnouncement} color="error" variant="contained">Elimina</Button>
                </DialogActions>
            </Dialog>

            {/* MASS CANCEL CONFIRM */}
            <Dialog open={isConfirmationOpen} onClose={() => setIsConfirmationOpen(false)}>
                <DialogTitle sx={{ color: '#d32f2f', fontWeight: 'bold' }}>⚠️ Conferma Chiusura</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Stai per cancellare <b>TUTTE</b> le prenotazioni dal <b>{cancellationStartDate}</b> al <b>{cancellationEndDate}</b>.
                        <br/><br/>
                        Gli utenti vedranno lo stato "Cancellato dall'Admin". Procedere?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsConfirmationOpen(false)}>Annulla</Button>
                    <Button onClick={handleMassCancellation} color="error" variant="contained">PROCEDI</Button>
                </DialogActions>
            </Dialog>

            {/* EDIT DIALOG */}
            <Dialog open={isEditing} onClose={() => setIsEditing(false)} fullWidth maxWidth="sm">
                <DialogTitle>Modifica Avviso</DialogTitle>
                <DialogContent>
                    {currentAnnouncement && (
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            <TextField
                                label="Titolo" fullWidth value={currentAnnouncement.title}
                                onChange={e => setCurrentAnnouncement({ ...currentAnnouncement, title: e.target.value })}
                            />
                            <TextField
                                label="Messaggio" fullWidth multiline rows={4} value={currentAnnouncement.message}
                                onChange={e => setCurrentAnnouncement({ ...currentAnnouncement, message: e.target.value })}
                            />
                            <TextField
                                label="Nuova Scadenza" type="date" fullWidth
                                value={currentAnnouncement.expirationDateStr || ''}
                                onChange={e => setCurrentAnnouncement({ ...currentAnnouncement, expirationDateStr: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsEditing(false)}>Annulla</Button>
                    <Button onClick={handleSaveEdit} variant="contained">Salva</Button>
                </DialogActions>
            </Dialog>

            {/* FEEDBACK SNACKBAR */}
            <Snackbar 
                open={feedback.open} autoHideDuration={4000} 
                onClose={() => setFeedback({ ...feedback, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={feedback.type} variant="filled" sx={{ width: '100%' }}>{feedback.msg}</Alert>
            </Snackbar>

        </MainContainer>
    );
}